'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { PlacesType } from 'react-tooltip';
import { normalizeMultiSelectFieldValue, useFormHelper } from '@plura/client/utils';
import { isMobile } from 'react-device-detect';
import cn from "classnames";
import { Icon } from "@plura/client/components";
import Tooltip from '../../atoms/Tooltip';
import './checkbox-group.css';
import { FormElementType, Media } from '@plura/shared/types';
import MediaDisplay from '../MediaDisplay';
import { useFormContext } from 'react-hook-form';
import { OTHER_OPTION_PREFIX } from '@plura/shared/utils';

const OTHER_PREFIX = OTHER_OPTION_PREFIX;

interface Option {
  value: string;
  label: string;
  media: Media | null;
}

type InputProps = React.InputHTMLAttributes<HTMLInputElement> &
{
  label?: string;
  error?: string;
  className?: string;
  classLabel?: string;
  classInput?: string;
  icon?: string;
  copy?: boolean;
  tooltip?: string;
  place?: PlacesType;
  trailingComponent?: React.ReactNode;
  options: Option[];
  disabledKeySelection?: boolean;
  /**
   * Hides the A/B/C letter chips and turns off the letter-key shortcuts,
   * while keeping the options fully clickable (unlike `disabledKeySelection`,
   * which disables the inputs entirely).
   */
  hideKeyHints?: boolean;
  multiple?: boolean;
  handleOnChange?: () => void;
  elementType?: FormElementType;
  layout?: 'horizontal' | 'vertical';
  showOtherOption?: boolean;
};

const CheckboxGroup = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    label,
    className,
    classLabel,
    classInput,
    icon,
    copy,
    tooltip,
    place,
    trailingComponent,
    options,
    disabledKeySelection,
    hideKeyHints,
    multiple,
    handleOnChange,
    elementType,
    layout,
    showOtherOption,
    ...rest
  } = props;
  const { onValueChange, errorMessage, onChange, ...formHelperProps } = useFormHelper({ ...rest, multiple });
  const form = useFormContext();

  const [otherEditing, setOtherEditing] = useState(false);
  const [otherText, setOtherText] = useState('');
  const otherInputRef = useRef<HTMLTextAreaElement>(null);

  const currentValue = formHelperProps.value;

  const multiValues = multiple ? normalizeMultiSelectFieldValue(currentValue) : [];

  // Check if "other" is currently selected in the form value
  const isOtherSelected = multiple
    ? multiValues.some((v) => v.startsWith(OTHER_PREFIX))
    : typeof currentValue === 'string' && currentValue.startsWith(OTHER_PREFIX);

  // Extract existing other text from the stored form value
  const existingOtherText = (() => {
    if (multiple) {
      const otherVal = multiValues.find((v) => v.startsWith(OTHER_PREFIX));
      return otherVal ? otherVal.slice(OTHER_PREFIX.length) : '';
    }
    if (typeof currentValue === 'string' && currentValue.startsWith(OTHER_PREFIX)) {
      return currentValue.slice(OTHER_PREFIX.length);
    }
    return '';
  })();

  // When "Other" is clicked: enter editing mode
  const handleOtherClick = useCallback(() => {
    if (disabledKeySelection) return;

    if (isOtherSelected) {
      // Already selected — toggle off if not editing, or re-enter edit mode
      if (!otherEditing) {
        // Deselect other
        if (multiple) {
          const filtered = normalizeMultiSelectFieldValue(form?.getValues(rest.id || '')).filter(
            (v) => !v.startsWith(OTHER_PREFIX),
          );
          form?.setValue(rest.id || '', filtered);
        } else {
          onValueChange('');
        }
        setOtherEditing(false);
        setOtherText('');
      }
      return;
    }

    // Select "other" and open the text input
    setOtherText('');
    setOtherEditing(true);

    if (!multiple) {
      // Deselect other options
      options.forEach(option => {
        const checkbox = document.getElementById(`${rest.id}.${option.value}`) as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      });
      onValueChange(`${OTHER_PREFIX}`);
    } else {
      // Add placeholder other value to the array (read fresh so we never drop selected options)
      const arr = normalizeMultiSelectFieldValue(form?.getValues(rest.id || ''));
      arr.push(`${OTHER_PREFIX}`);
      form?.setValue(rest.id || '', arr);
    }

    setTimeout(() => otherInputRef.current?.focus(), 50);
  }, [isOtherSelected, otherEditing, disabledKeySelection, multiple, options, rest.id, onValueChange, form]);

  // Commit the "Other" text value
  const handleOtherSubmit = useCallback(() => {
    const trimmed = otherText.trim();

    if (!trimmed) {
      // Empty text — deselect other entirely
      if (multiple) {
        const filtered = normalizeMultiSelectFieldValue(form?.getValues(rest.id || '')).filter(
          (v) => !v.startsWith(OTHER_PREFIX),
        );
        form?.setValue(rest.id || '', filtered);
      } else {
        onValueChange('');
      }
      setOtherEditing(false);
      setOtherText('');
      return;
    }

    const otherValue = `${OTHER_PREFIX}${trimmed}`;

    if (multiple) {
      const filtered = normalizeMultiSelectFieldValue(form?.getValues(rest.id || '')).filter(
        (v) => !v.startsWith(OTHER_PREFIX),
      );
      filtered.push(otherValue);
      form?.setValue(rest.id || '', filtered);
    } else {
      onValueChange(otherValue);
    }

    setOtherEditing(false);
    if (handleOnChange) setTimeout(() => handleOnChange(), 450);
  }, [otherText, multiple, rest.id, onValueChange, form, handleOnChange]);

  // When a normal option is selected, clear "other" editing state and deselect other in single-select
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // If single-select and other was selected, clear it
    if (!multiple && isOtherSelected) {
      setOtherEditing(false);
      setOtherText('');
    }

    if (multiple) {
      onChange(event);
    } else {
      options.forEach(option => {
        const checkbox = document.getElementById(`${rest.id}.${option.value}`) as HTMLInputElement;
        if (checkbox && checkbox !== event.target) {
          checkbox.checked = false;
        }
      });

      onValueChange(event.target.value);
      if (handleOnChange) setTimeout(() => handleOnChange(), 450);
    }
  };

  useEffect(() => {
    if (hideKeyHints) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isMobile && !disabledKeySelection) {
        if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
          return;
        }

        // Named keys ('Enter', 'Escape', 'ArrowDown', …) must never map to
        // a letter index — 'Enter'.charCodeAt(0) reads as 'e' and used to
        // select option E. This also closes a race with the "Other" input:
        // Enter there submits + unmounts the textarea synchronously, so by
        // the time this window listener sees the same native event the
        // activeElement guard below no longer applies.
        if (event.key.length !== 1) {
          return;
        }

        const el = document.activeElement;
        if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || (el as HTMLElement).isContentEditable)) {
          return;
        }

        const index = event.key.toLowerCase().charCodeAt(0) - 97;
        if (index < 0 || index > 25) {
          return;
        }
        const option = options[index];

        if (option) {
          const checkbox = document.getElementById(`${rest.id}.${option.value}`) as HTMLInputElement;

          if (checkbox) checkbox.click();
        } else if (showOtherOption && index === options.length) {
          handleOtherClick();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [options, rest.id, onValueChange, multiple, disabledKeySelection, hideKeyHints, showOtherOption, handleOtherClick]);

  return (
    <div className={cn(className)}>
      {label && (
        <label className={cn(classLabel, 'mb-3.5 block text-sm font-semibold leading-[24px] text-[#33383f] [.dark-mode_&]:text-[#efefef]')}>
          {label}{" "}
          {tooltip && (
            <Tooltip
              className="relative -top-px [&_svg]:fill-[#9a9fa5]"
              content={tooltip}
              place={place ? place : "right"}
            />
          )}
        </label>
      )}
      <div className="plura-cbg-wrap">
        <div className={cn('plura-cbg-list', layout === 'horizontal' && 'plura-cbg-list--horizontal', hideKeyHints && 'plura-cbg-list--no-letters')}>
          {options.map((option) => {
            const watched = formHelperProps.value;
            const isChecked = multiple
              ? normalizeMultiSelectFieldValue(watched).includes(option.value)
              : watched === option.value;

            return (
              <div
                key={option.value}
                className={cn(
                  'plura-cbg-item',
                  option.media && 'plura-cbg-item--has-media',
                  option.media && elementType === 'SQUARED' && 'plura-cbg-item--squared',
                )}
              >
                <input
                  id={`${rest.id}.${option.value}`}
                  type="checkbox"
                  {...formHelperProps}
                  disabled={disabledKeySelection}
                  name={`${rest.id}.${option.value}`}
                  value={option.value}
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  className="plura-cbg-input"
                />
                <label htmlFor={`${rest.id}.${option.value}`} className="plura-cbg-label">
                  {
                    option.media &&
                    <div className="plura-cbg-media">
                      {
                        option.media.type !== 'video' ? (
                          <img
                            src={`${option.media.link}?q=60&w=300`}
                            alt={option.media.name}
                          />
                        ) : (
                          <video
                            src={option.media.link}
                            autoPlay
                            loop
                            muted
                          />
                        )
                      }
                    </div>
                  }
                  <div className="plura-cbg-option-label">
                    <b><span>Key</span></b>
                    <span>{option.label}</span>
                    <Icon name="check" className="plura-cbg-check-icon fill-current" />
                  </div>
                </label>
              </div>
            );
          })}

          {/* "Other" option */}
          {showOtherOption && (
            <div
              className={cn(
                'plura-cbg-item',
                'plura-cbg-item--other',
                isOtherSelected && 'plura-cbg-item--other-active',
              )}
            >
              {otherEditing ? (
                <div className="plura-cbg-label plura-cbg-other-editing">
                  <div className="plura-cbg-option-label">
                    <b><span>Key</span></b>
                    <textarea
                      ref={otherInputRef}
                      className="plura-cbg-other-input"
                      value={otherText}
                      onChange={(e) => setOtherText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleOtherSubmit();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          if (!otherText.trim() && !existingOtherText) {
                            // Cancel — deselect other
                            if (multiple) {
                              const filtered = normalizeMultiSelectFieldValue(form?.getValues(rest.id || '')).filter(
                                (v) => !v.startsWith(OTHER_PREFIX),
                              );
                              form?.setValue(rest.id || '', filtered);
                            } else {
                              onValueChange('');
                            }
                          }
                          setOtherEditing(false);
                        }
                      }}
                      placeholder="Type your answer..."
                      rows={1}
                    />
                    <button
                      type="button"
                      className="plura-cbg-other-submit"
                      onClick={handleOtherSubmit}
                      aria-label="Submit"
                    >
                      <Icon name="check" className="plura-cbg-check-icon fill-current" />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className="plura-cbg-label"
                    role="button"
                    tabIndex={0}
                    onClick={handleOtherClick}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOtherClick();
                      }
                    }}
                  >
                    <div className="plura-cbg-option-label">
                      <b><span>Key</span></b>
                      <span>{isOtherSelected && existingOtherText ? existingOtherText : 'Other'}</span>
                      {isOtherSelected && (
                        <Icon name="check" className="plura-cbg-check-icon fill-current" style={{ opacity: 1 }} />
                      )}
                    </div>
                  </div>
                  {/* Hidden checkbox to participate in the checked styling */}
                  <input
                    type="checkbox"
                    checked={isOtherSelected}
                    readOnly
                    className="plura-cbg-input"
                    tabIndex={-1}
                  />
                </>
              )}
            </div>
          )}
        </div>

        {icon && (
          <div className={cn('icon absolute bottom-0 left-0 top-0 flex h-12 w-12 items-center justify-center')}>
            <i className={icon} />{" "}
          </div>
        )}
        {copy && (
          <button type="button" className="absolute bottom-0 right-0 top-0 flex h-12 w-12 items-center justify-center [&_svg]:fill-[#6f767e] hover:[&_svg]:fill-[#2a85ff]">
            <Icon name="copy" className="!size-6" />{" "}
          </button>
        )}

        {
          trailingComponent && (
            <div className="absolute right-1 top-1 z-[2]">
              {trailingComponent}
            </div>
          )
        }

        {errorMessage && (
          <p className="mt-1 text-[13px] text-[#ff6a55]">{errorMessage}</p>
        )}
      </div>
    </div>
  );
});

CheckboxGroup.displayName = 'CheckboxGroup';

export default CheckboxGroup
